import type { UserInfoResponse } from '@chat-types/model/user/UserInfoResponse';
import useDebounce from '@hooks/useDebounce';
import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import useModalHook from '@hooks/useModalHook';
import { useGetUserListQuery } from '@queries/hooks/useGetUserListQuery';
import useUserListStore from '@stores/userListStore';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import strings from '@i18n/index';
import { useErrorHandler } from '@hooks/useErrorHandler';
import UserChipItem from '@components/channelList/UserChipItem';
import useCurrentChannel from '@stores/currentChannelStore';
import { cloneDeep } from 'lodash-es';
import type { AddMemberModalProps } from '@views/channelSettings/AddMemberModal';

export const useAddMemberModal = ({ channel }: AddMemberModalProps) => {
  const { closeModal } = useModalHook();
  const { showServerError, openErrorAlert } = useErrorHandler();
  const { setCurrentChannel } = useCurrentChannel();

  const scrollRef = useRef<HTMLDivElement>(null);
  const targetObserverRef = useRef<HTMLDivElement>(null);

  const [scrollHeight, setScrollHeight] = useState(0);
  const [offsetHeight, setOffsetHeight] = useState(0);

  const {
    records,
    searchName,
    selectedIds,
    selectedUsers,
    setSearchName,
    resetSearchName,
    setSelectedUsers,
    clearState,
  } = useUserListStore();

  const debounceSearchValue = useDebounce(searchName, 300);

  const {
    data: userList,
    total,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetUserListQuery({
    records,
    searchName: debounceSearchValue,
  });

  useEffect(() => {
    setTimeout(() => {
      setScrollHeight(scrollRef.current?.scrollHeight || 0);
      setOffsetHeight(scrollRef.current?.offsetHeight || 0);
    }, 300);
    // eslint-disable-next-line
    }, [scrollRef, scrollHeight, offsetHeight]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRef, scrollRef.current, searchName]);

  const resetLabel = useMemo(() => {
    const selectedText = strings.SENDBIRD.MODAL__CREATE_CHANNEL__SELECTED;
    return `<span>${selectedIds.length}</span> / ${total} ${selectedText}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  const onIntersect = useCallback(
    async (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      const entry = entries[0];
      if (entry.isIntersecting && hasNextPage) {
        observer.unobserve(entry.target);
        if (hasNextPage) {
          await fetchNextPage();
        }
        observer.observe(entry.target);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useIntersectionObserver(targetObserverRef, onIntersect, {
    threshold: 1.0,
  });

  const closeModalHandler = () => {
    clearState(); // state clear
    closeModal('addMemberModal');
  };

  const confirmModalHandler = async () => {
    try {
      // selectedIds 가 0명이면 오류 팝업
      if (selectedIds.length === 0) {
        openErrorAlert({
          depth: 2,
          message: strings.ALERT__NO_ONE_SELECTED,
        });
        return;
      }

      // selectedIds 가 20,000 이상이면 오류 팝업
      if (selectedIds.length > 20000) {
        openErrorAlert({
          depth: 2,
          message: strings.ALERT__MAX_INVITE_MEMBER,
        });
        return;
      }

      const updatedChannel = await channel.inviteWithUserIds(selectedIds);
      // MARK ::: object 값 변경 감지 위한 깊은 복사
      if (updatedChannel) {
        setCurrentChannel(cloneDeep(updatedChannel));
      }
    } catch (error) {
      // console.warn({ error });
      showServerError();
    } finally {
      closeModalHandler();
    }
  };

  const changeInputHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchName(e.target.value);
  };

  const selectTargetUserHandler = (selected: UserInfoResponse) => {
    if (selectedIds.includes(selected.userId)) {
      // MARK 동일 id 존재할 때,
      setSelectedUsers(selectedUsers.filter((origin) => origin.userId !== selected.userId));
      return;
    }
    setSelectedUsers([...selectedUsers, selected]);
  };

  const resetInputHandler = () => resetSearchName();
  const resetTargetUserHandler = () => clearState();

  const renderSelectedUserChips = (renderList: UserInfoResponse[]) => {
    return (
      renderList &&
      renderList.map((user, index) => (
        <UserChipItem
          key={`chips__${user.userId}${index}`}
          user={user}
          index={index}
          onClickDelete={() => {
            selectTargetUserHandler(user);
          }}
        />
      ))
    );
  };

  return {
    userList,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    searchName,
    selectedIds,
    selectedUsers,
    resetLabel,
    hasScroll: scrollHeight > offsetHeight,
    scrollRef,
    targetObserverRef,
    closeModalHandler,
    confirmModalHandler,
    changeInputHandler,
    resetInputHandler,
    selectTargetUserHandler,
    resetTargetUserHandler,
    renderSelectedUserChips,
  };
};
